// App.js - Refactored Version with Preloading & Forced Audio Load

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import './App.css';
import { FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import DualWaveformVisualizer from './components/DualWaveformVisualizer';
import logo from './assets/images/docMJ-logo1.png';

let audioContext;

function App() {
  // State Variables
  const [isConnected, setIsConnected] = useState(false);
  const [status, setStatus] = useState('Disconnected');
  const [isListening, setIsListening] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [aiAudioElement, setAiAudioElement] = useState(null);
  const [debugMode] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [useBrowserSpeechRecognition, setUseBrowserSpeechRecognition] = useState(false);
  const [useStreamingSTT, setUseStreamingSTT] = useState(false);
  const [interimTranscription, setInterimTranscription] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);

  // Refs for various objects
  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const userStreamRef = useRef(null);
  const speechRecognitionRef = useRef(null);
  const isStreamingRef = useRef(false);
  const logsRef = useRef(null);

  const getAudioContext = () => {
    try {
      if (!audioContext || audioContext.state === 'closed') {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        // Add error event listener to detect audio device issues
        audioContext.addEventListener('statechange', () => {
          console.log('AudioContext state changed to:', audioContext.state);
          if (audioContext.state === 'interrupted' || audioContext.state === 'closed') {
            setAudioError('Audio device connection lost');
          }
        });
      }
      return audioContext;
    } catch (error) {
      console.error('Failed to create AudioContext:', error);
      setAudioError('Audio system unavailable');
      throw error;
    }
  };

  const initializeAudioContext = useCallback(async () => {
    try {
      const context = getAudioContext();
      
      // Handle different AudioContext states
      if (context.state === 'suspended') {
        await context.resume();
        console.log('AudioContext resumed successfully');
      } else if (context.state === 'closed') {
        // If context is closed, create a new one
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        console.log('Created new AudioContext after previous was closed');
      }
      
      // Additional check for audio device errors
      if (context.state === 'interrupted') {
        console.warn('AudioContext is interrupted, attempting recovery');
        // Wait a bit and try to resume
        await new Promise(resolve => setTimeout(resolve, 100));
        await context.resume();
      }
      
      return context;
    } catch (error) {
      console.error('AudioContext initialization error:', error);
      
      // Try to create a completely new AudioContext as fallback
      try {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        console.log('Created fallback AudioContext');
        return audioContext;
      } catch (fallbackError) {
        console.error('Failed to create fallback AudioContext:', fallbackError);
        throw new Error('AudioContext creation failed: ' + fallbackError.message);
      }
    }
  }, []);

  // Convert base64 audio to a playable blob, preloads, and then plays it
  const playAudioResponse = useCallback((base64Audio) => {
    try {
      if (!base64Audio || base64Audio.length === 0) {
        console.warn('Empty audio data received');
        setStatus('Ready');
        return;
      }

      // Base64 to binary conversion
      const binaryString = atob(base64Audio);
      const len = binaryString.length;
      const buffer = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        buffer[i] = binaryString.charCodeAt(i);
      }

      const audioBlob = new Blob([buffer.buffer], { type: 'audio/mp3' });
      const audioUrl = URL.createObjectURL(audioBlob);

      const newAudio = new Audio();
      newAudio.preload = 'auto';
      newAudio.src = audioUrl;
      newAudio.volume = 1.0;

      newAudio.onplay = () => {
        setIsPlaying(true);
        setIsAiSpeaking(true);
      };

      newAudio.onended = () => {
        setIsPlaying(false);
        setIsAiSpeaking(false);
        setStatus('Ready');
      };

      newAudio.onerror = (e) => {
        console.error('Audio playback error:', e);
        setIsPlaying(false);
        setIsAiSpeaking(false);
        setStatus('Error playing audio');
      };

      setAiAudioElement(newAudio);

      // Delay to ensure preloading and then play
      setTimeout(() => {
        newAudio
          .play()
          .catch((err) => {
            console.error('Error starting audio playback:', err);
            if (err.name === 'NotAllowedError') {
              const resumeAudio = async () => {
                try {
                  await initializeAudioContext();
                  await newAudio.play();
                } catch (error) {
                  console.error('Failed to play audio after user gesture:', error);
                }
                document.body.removeEventListener('click', resumeAudio);
              };
              document.body.addEventListener('click', resumeAudio, { once: true });
            }
          });
      }, 100);
    } catch (error) {
      console.error('Error processing audio response:', error);
      setIsPlaying(false);
      setIsAiSpeaking(false);
      setStatus('Error playing response');
    }
  }, [setStatus, setIsPlaying, setIsAiSpeaking, setAiAudioElement, initializeAudioContext]);

  // Setup Socket Connection & Event Handlers
  useEffect(() => {
    const socketURL =
      process.env.NODE_ENV === 'production'
        ? undefined
        : process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';

    socketRef.current = io(socketURL);

    // Handle Socket Connection Events
    socketRef.current.on('connect', () => {
      setIsConnected(true);
      setStatus('Connected');
    });

    socketRef.current.on('disconnect', () => {
      setIsConnected(false);
      setStatus('Disconnected');
      setIsListening(false);
      setIsUserSpeaking(false);
    });

    // Handle Incoming Transcriptions from User
    socketRef.current.on('transcription', (data) => {
      if (data?.text) {
        setConversation((prev) => [...prev, { role: 'user', text: data.text }]);
      }
    });

    // Handle logs from server
    socketRef.current.on('serverLog', (data) => {
      const timestamp = new Date().toLocaleTimeString();
      const logEntry = {
        timestamp,
        level: data.level || 'info',
        message: data.message,
        sessionId: data.sessionId
      };
      
      setLogs(prev => {
        const newLogs = [...prev, logEntry];
        // Keep only last 100 log entries
        return newLogs.slice(-100);
      });
      
      // Auto-scroll to bottom if logs are visible
      if (logsRef.current) {
        setTimeout(() => {
          logsRef.current.scrollTop = logsRef.current.scrollHeight;
        }, 10);
      }
    });

    // Handle streaming transcription results
    socketRef.current.on('streamingTranscription', (data) => {
      console.log(`[STREAMING] ${data.isFinal ? 'FINAL' : 'INTERIM'}: ${data.text}`);
      
      if (data.isFinal) {
        // Add final transcription to conversation
        setConversation(prev => [...prev, {
          type: 'user',
          text: data.text,
          timestamp: new Date().toLocaleTimeString()
        }]);
        setInterimTranscription('');
        setStatus('Processing...');
      } else {
        // Show interim results
        setInterimTranscription(data.text);
        setStatus(`Listening... "${data.text}"`);
      }
    });

    // Handle AI Responses
    socketRef.current.on('aiResponse', (data) => {
      const responseTime = Date.now();
      console.log(`[LATENCY] AI response received at ${new Date().toISOString()}`);
      
      if (data.processingTime) {
        console.log(`[LATENCY] Server processing time: ${data.processingTime}ms`);
      }
      
      console.log('AI response received:', {
        hasText: !!data.text,
        textLength: data.text ? data.text.length : 0,
        hasAudio: !!data.audio,
        audioLength: data.audio ? data.audio.length : 0,
        processingTime: data.processingTime
      });

      if (data?.text) {
        const cleanText = data.text.replace(/<[^>]*>|<\/[^>]*>/g, '');
        setConversation((prev) => [...prev, { role: 'assistant', text: cleanText }]);
      }
      setStatus('Ready');

      if (data?.audio) {
        playAudioResponse(data.audio);
      } else {
        console.log('No audio in response');
      }

      setIsListening(false);
      setIsUserSpeaking(false);
    });

    // Handle Errors
    socketRef.current.on('error', (error) => {
      console.error('Socket error:', error);
      setIsListening(false);
      setIsUserSpeaking(false);
      setIsPlaying(false);
      setIsAiSpeaking(false);
      setStatus('Error occurred');
    });

    // Cleanup on Unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      stopListening();
    };
  }, [playAudioResponse]);

  // Browser Speech Recognition (fallback/alternative method)
  const startBrowserSpeechRecognition = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setStatus('Browser speech recognition not supported');
      return;
    }

    const startTime = Date.now();
    console.log(`[LATENCY] Browser speech recognition started at ${new Date().toISOString()}`);

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setIsUserSpeaking(true);
      setStatus('Browser listening... Speak now!');
      console.log('Browser speech recognition started');
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      const confidence = event.results[0][0].confidence;
      const recognitionTime = Date.now() - startTime;
      
      console.log(`[LATENCY] Browser speech recognition completed in ${recognitionTime}ms`);
      console.log('Browser speech recognition result:', transcript, 'confidence:', confidence);
      
      // Add to conversation
      setConversation(prev => [...prev, {
        role: 'user',
        text: transcript,
        timestamp: new Date().toLocaleTimeString()
      }]);

      // Send directly to AI (bypass the audio processing)
      if (socketRef.current) {
        const sendTime = Date.now();
        console.log(`[LATENCY] Sending transcription to server at ${new Date().toISOString()}`);
        
        socketRef.current.emit('directTranscription', { 
          text: transcript, 
          confidence: confidence,
          source: 'browser_speech_recognition',
          clientStartTime: startTime,
          clientSendTime: sendTime
        });
      }

      setIsListening(false);
      setIsUserSpeaking(false);
      setStatus('Processing...');
    };

    recognition.onerror = (event) => {
      console.error('Browser speech recognition error:', event.error);
      setStatus(`Speech recognition error: ${event.error}`);
      setIsListening(false);
      setIsUserSpeaking(false);
    };

    recognition.onend = () => {
      console.log('Browser speech recognition ended');
      if (isListening) {
        setIsListening(false);
        setIsUserSpeaking(false);
        setStatus('Ready');
      }
    };

    speechRecognitionRef.current = recognition;
    recognition.start();
  };

  // Start capturing audio from user's microphone
  const startListening = async () => {
    console.log('[DEBUG] startListening called, useBrowserSpeechRecognition:', useBrowserSpeechRecognition);
    
    // If browser speech recognition is enabled, use that instead
    if (useBrowserSpeechRecognition) {
      console.log('[DEBUG] Using browser speech recognition');
      startBrowserSpeechRecognition();
      return;
    }
    
    console.log('[DEBUG] Using regular audio recording');

    try {
      // Initialize AudioContext first (user gesture has already occurred)
      await initializeAudioContext();
      
      // Request microphone permission and get stream with more robust settings
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 16000, min: 8000, max: 48000 }, // Prefer 16kHz for speech
          channelCount: 1, // Use mono to reduce processing load
          volume: 1.0,     // Ensure full volume
          latency: 0.1     // Low latency for real-time
        } 
      });
      userStreamRef.current = stream;

      // Check MediaRecorder support and choose best format
      // Try different formats in order of reliability for speech recognition
      let mimeType = 'audio/wav'; // Try WAV first for better compatibility
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm;codecs=opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/webm';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = 'audio/mp4';
            if (!MediaRecorder.isTypeSupported(mimeType)) {
              mimeType = 'audio/ogg;codecs=opus';
              if (!MediaRecorder.isTypeSupported(mimeType)) {
                mimeType = undefined; // Use default
                console.warn('No preferred audio formats supported, using default');
              }
            }
          }
        }
      }
      
      console.log('Using MediaRecorder mimeType:', mimeType || 'default');

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 128000 // Set explicit bitrate for better quality
      });
      audioChunksRef.current = [];
      
      // Add audio level monitoring to detect if we're actually getting sound
      const audioContext = getAudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      microphone.connect(analyser);
      
      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      let audioLevelCheckCount = 0;
      let hasDetectedSound = false;
      
      const checkAudioLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
        
        if (average > 10) { // Threshold for detecting sound
          hasDetectedSound = true;
          console.log('Audio level detected:', average);
        }
        
        audioLevelCheckCount++;
        if (audioLevelCheckCount < 50 && isListening) { // Check for ~5 seconds
          setTimeout(checkAudioLevel, 100);
        } else if (!hasDetectedSound && isListening) {
          console.warn('No significant audio levels detected during recording');
          setStatus('No audio detected - please check your microphone');
        }
      };
      
      setTimeout(checkAudioLevel, 500); // Start checking after 500ms

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log('Audio chunk received, size:', event.data.size);
          audioChunksRef.current.push(event.data);
          
          // If streaming is enabled, send chunks immediately
          if (useStreamingSTT && isStreamingRef.current) {
            sendAudioChunkForStreaming(event.data);
          }
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        console.log('MediaRecorder stopped, total chunks:', audioChunksRef.current.length);
        const totalSize = audioChunksRef.current.reduce((sum, chunk) => sum + chunk.size, 0);
        console.log('Total audio data size:', totalSize, 'bytes');
        
        // End streaming if it was active
        if (isStreamingRef.current) {
          isStreamingRef.current = false;
          if (socketRef.current) {
            socketRef.current.emit('endStreaming');
          }
          console.log('[STREAMING] Ended streaming session');
          return; // Don't send regular audio blob for streaming mode
        }
        
        // Check recording duration
        const recordingDuration = recordingStartTime ? (Date.now() - recordingStartTime) / 1000 : 0;
        console.log('Recording duration:', recordingDuration, 'seconds');
        
        if (totalSize === 0) {
          console.warn('No audio data captured');
          setStatus('No audio captured - please try speaking louder');
          return;
        }
        
        // Reduced minimum duration for faster response (optimized for low latency)
        if (recordingDuration < 0.5) {
          console.warn('Recording duration is very short:', recordingDuration, 'seconds');
          setStatus('Recording too short - please speak clearly');
          return;
        }
        
        // Check if audio data is sufficient (rough estimate: 1KB per second minimum)
        const expectedMinSize = recordingDuration * 1000; // 1KB per second minimum
        if (totalSize < expectedMinSize) {
          console.warn('Audio data seems insufficient for duration. Size:', totalSize, 'Expected min:', expectedMinSize);
        }

        const audioBlob = new Blob(audioChunksRef.current, { 
          type: mimeType || 'audio/webm' 
        });
        console.log('Created audio blob, size:', audioBlob.size, 'type:', audioBlob.type, 'duration:', recordingDuration + 's');
        await sendAudioToServer(audioBlob);
      };

      // Configure recording intervals based on streaming mode
      const recordingInterval = useStreamingSTT ? 250 : 500; // Shorter intervals for streaming
      mediaRecorderRef.current.start(recordingInterval);
      
      // Start streaming mode if enabled
      if (useStreamingSTT) {
        isStreamingRef.current = true;
        console.log('[STREAMING] Started streaming mode');
      }
      
      setIsListening(true);
      setIsUserSpeaking(true);
      setRecordingStartTime(Date.now());
      setStatus(useStreamingSTT ? 'Streaming... speak now!' : 'Listening... speak clearly!');
    } catch (error) {
      console.error('Error starting recording:', error);
      
      // Clean up any partial state
      setIsListening(false);
      setIsUserSpeaking(false);
      
      // More specific error messages
      if (error.name === 'NotAllowedError') {
        setStatus('Microphone access denied. Please allow microphone access and try again.');
      } else if (error.name === 'NotFoundError') {
        setStatus('No microphone found. Please connect a microphone and try again.');
      } else if (error.name === 'NotReadableError') {
        setStatus('Microphone is busy or unavailable. Please close other apps using the microphone and try again.');
      } else if (error.name === 'NotSupportedError') {
        setStatus('Browser doesn\'t support audio recording. Please try a different browser.');
      } else if (error.message.includes('AudioContext')) {
        setStatus('Audio system error. Please refresh the page or restart your browser.');
      } else {
        setStatus(`Error accessing audio: ${error.message}`);
      }
    }
  };

  // Stop the audio recording process
  const stopListening = () => {
    // Stop browser speech recognition if active
    if (speechRecognitionRef.current) {
      speechRecognitionRef.current.stop();
      speechRecognitionRef.current = null;
      setIsListening(false);
      setIsUserSpeaking(false);
      setStatus('Ready');
      return;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      // Check if we've been recording for a minimum amount of time (reduced for low latency)
      const recordingDuration = recordingStartTime ? (Date.now() - recordingStartTime) / 1000 : 0;
      
      if (recordingDuration < 0.8) {
        console.log('Recording duration too short:', recordingDuration, 'seconds. Continuing to record...');
        setStatus('Please continue speaking...');
        return; // Don't stop recording yet
      }
      
      console.log('Stopping MediaRecorder, current state:', mediaRecorderRef.current.state);
      console.log('Recording duration:', recordingDuration, 'seconds');
      mediaRecorderRef.current.stop();
      
      // Stop all tracks in the stream
      if (userStreamRef.current) {
        userStreamRef.current.getTracks().forEach((track) => {
          console.log('Stopping track:', track.kind, track.label);
          track.stop();
        });
      }
      
      setIsListening(false);
      setIsUserSpeaking(false);
      setStatus('Processing...');
    } else {
      console.log('MediaRecorder not active, current state:', 
        mediaRecorderRef.current ? mediaRecorderRef.current.state : 'null');
    }
  };

  // Recovery function for audio issues
  const recoverAudioContext = async () => {
    try {
      // Close existing context if it exists
      if (audioContext && audioContext.state !== 'closed') {
        await audioContext.close();
      }
      
      // Reset context and create new one
      audioContext = null;
      setAudioError(null);
      
      // Force recreation on next use
      await initializeAudioContext();
      setStatus('Audio system recovered');
      
      return true;
    } catch (error) {
      console.error('Audio recovery failed:', error);
      setAudioError('Audio recovery failed');
      return false;
    }
  };

  // Toggle between starting and stopping audio capture
  const toggleListening = async () => {
    if (isListening) {
      // Check if we've recorded for at least 1 second
      const recordingDuration = recordingStartTime ? Date.now() - recordingStartTime : 0;
      if (recordingDuration < 1000) {
        console.log('Recording duration too short:', recordingDuration, 'ms - continuing to record');
        setStatus('Please speak for at least 1 second...');
        return;
      }
      stopListening();
    } else {
      // If there's an audio error, try to recover first
      if (audioError) {
        const recovered = await recoverAudioContext();
        if (!recovered) {
          setStatus('Please refresh the page to reset audio system');
          return;
        }
      }
      
      // This function is called from a user click, so we can safely create/resume AudioContext
      await startListening();
    }
  };

  

  // Send individual audio chunks for streaming transcription
  const sendAudioChunkForStreaming = async (audioChunk) => {
    try {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Audio = reader.result.split(',')[1];
        if (socketRef.current && isStreamingRef.current) {
          socketRef.current.emit('streamingAudio', { audioChunk: base64Audio });
        }
      };
      reader.readAsDataURL(audioChunk);
    } catch (error) {
      console.error('Error sending streaming audio chunk:', error);
    }
  };

  // Convert audio blob to base64 and emit to server
  const sendAudioToServer = async (audioBlob) => {
    try {
      console.log('Sending audio to server - blob size:', audioBlob.size, 'type:', audioBlob.type);
      
      // Validate audio blob
      if (!audioBlob || audioBlob.size === 0) {
        console.error('Invalid audio blob - size is 0');
        setStatus('No audio data to send');
        return;
      }
      
      // Minimum size check (rough estimate: ~1KB for very short audio)
      if (audioBlob.size < 1000) {
        console.warn('Audio blob is very small, may not contain enough data for transcription');
      }

      const reader = new FileReader();
      reader.onload = async () => {
        const base64Audio = reader.result.split(',')[1];
        console.log('Base64 audio length:', base64Audio.length);
        
        if (!base64Audio || base64Audio.length === 0) {
          console.error('Failed to convert audio to base64');
          setStatus('Error processing audio');
          return;
        }
        
        console.log('Emitting audio chunk to server via WebSocket');
        socketRef.current.emit('audioChunk', { audioChunk: base64Audio });
      };
      
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        setStatus('Error reading audio file');
      };
      
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.error('Error sending audio to server:', error);
      setStatus('Error sending audio');
    }
  };

  // Debug logging if enabled
  if (debugMode) {
    console.log('Detailed state:', {
      isConnected,
      isListening,
      isUserSpeaking,
      isPlaying,
      isAiSpeaking,
      aiAudioElementCreated: !!aiAudioElement,
    });
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-200 flex flex-col">
      <header className="bg-white shadow-md p-4">
        <div className="flex justify-between items-center">
          <img src={logo} alt="Company Logo" className="logo items-center justify-left" />
          
          {/* Speech Recognition Method Toggle */}
          <div className="flex flex-col items-end gap-2 text-sm">
            <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-full">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useBrowserSpeechRecognition}
                  onChange={(e) => setUseBrowserSpeechRecognition(e.target.checked)}
                  className="w-4 h-4"
                />
                Browser Speech Recognition
                {useBrowserSpeechRecognition && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) && 
                  <span style={{ color: '#4CAF50' }}>✓</span>
                }
              </label>
            </div>
            
            {!useBrowserSpeechRecognition && (
              <div className="flex items-center gap-2 bg-blue-100 px-3 py-2 rounded-full">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useStreamingSTT}
                    onChange={(e) => setUseStreamingSTT(e.target.checked)}
                    className="w-4 h-4"
                  />
                  Streaming STT (Low Latency)
                  {useStreamingSTT && <span style={{ color: '#4CAF50' }}>⚡</span>}
                </label>
              </div>
            )}
            
            <div className="flex items-center gap-2 bg-gray-200 px-3 py-2 rounded-full">
              <button
                onClick={() => setShowLogs(!showLogs)}
                className="flex items-center gap-2 text-sm cursor-pointer hover:text-blue-600"
              >
                📊 {showLogs ? 'Hide' : 'Show'} Logs
              </button>
            </div>
          </div>
        </div>
        
        <div className="relative flex items-center text-white justify-center mt-4">
          <DualWaveformVisualizer
            userStream={userStreamRef.current}
            aiAudio={aiAudioElement}
            isUserSpeaking={isUserSpeaking || isListening}
            isAiSpeaking={isPlaying}
            audioContext={getAudioContext()}
          />
        </div>
      </header>

      <main className="flex-1 container mx-auto justify-right p-4">
        <div className="flex flex-col items-center gap-2">
          <div className={`flex items-center px-3 py-1 rounded-full ${isListening
            ? 'bg-red-100 text-red-800 animate-pulse'
            : isAiSpeaking
              ? 'bg-blue-100 text-blue-800 animate-pulse'
              : isConnected
                ? 'bg-green-100 text-dark-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
          <div
              className={`h-3 w-3 rounded-full mr-2 justify-left ${isListening ? 'bg-red-500' : isAiSpeaking ? 'bg-blue-500' : isConnected ? 'bg-green-500' : 'bg-gray-500'}`}></div>
            <span className="text-sm font-medium">{status}</span>
          </div>
          
          {/* Show interim transcription for streaming mode */}
          {interimTranscription && useStreamingSTT && (
            <div className="bg-yellow-100 text-yellow-800 px-3 py-2 rounded-lg text-sm max-w-md text-center">
              <span className="font-medium">Transcribing: </span>
              <span className="italic">"{interimTranscription}"</span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-left">
          <button
            onClick={toggleListening}
            disabled={!isConnected || isAiSpeaking}
            className={`
              w-20 h-20 rounded-full flex items-center justify-center
              ${isListening ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-900 text-white'}
              ${(!isConnected || isAiSpeaking) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              transition-all duration-200
            `}>
            {isListening ? <FaMicrophoneSlash className="text-3xl" /> : <FaMicrophone className="text-3xl" />}
          </button>
          <div className="flex items-center justify-center action-text">
            <p className="text-white">
              {isListening
                ? "I'm listening... Speak now!"
                : isAiSpeaking
                  ? "DocMJ Assistant is speaking..."
                  : audioError
                    ? `Audio Error: ${audioError} - Click to retry`
                    : status.includes('microphone') || status.includes('Microphone') || status.includes('Audio') || status.includes('audio')
                      ? status
                      : "Click the microphone button to start speaking"}
            </p>
          </div>
        </div>
        {conversation.length > 0 && (
          <div className="border rounded-md p-2 max-h-150 overflow-y-auto">
            <h3 className="font-semibold text-white">Conversation:</h3>
            {[...conversation].reverse().map((message, index) => (
              // Reverse the conversation for display
              <div key={index} className="mb-2">
                <div className={`rounded-lg ${message.role === 'user' ? 'bg-green-50' : 'bg-blue-50'}`}>
                  <p className={`text-sm p-1 font-bold ${message.role === 'user' ? 'text-speaking-user' : 'text-speaking-ai'}`}>
                    {message.role === 'user' ? 'You:' : 'DocMJ Assistant:'}
                  </p>
                  <p className="text-md p-3 font-semibold items-center justify-left">{message.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Real-time Logs Panel */}
        {showLogs && (
          <div className="mt-4 border-t pt-4">
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-xs">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-bold text-white">Server Logs (Real-time)</h3>
                <button
                  onClick={() => setLogs([])}
                  className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
                >
                  Clear
                </button>
              </div>
              <div
                ref={logsRef}
                className="h-64 overflow-y-auto bg-black p-2 rounded border"
                style={{ scrollBehavior: 'smooth' }}
              >
                {logs.length === 0 ? (
                  <div className="text-gray-500 italic">No logs yet... Start using the voice assistant to see real-time logs.</div>
                ) : (
                  logs.map((log, index) => (
                    <div key={index} className={`mb-1 ${
                      log.level === 'error' ? 'text-red-400' :
                      log.message.includes('[LATENCY]') ? 'text-yellow-400' :
                      log.message.includes('Connected') ? 'text-green-400' :
                      log.message.includes('AI Response') ? 'text-blue-400' :
                      'text-gray-300'
                    }`}>
                      <span className="text-gray-500">[{log.timestamp}]</span>
                      {log.sessionId && <span className="text-purple-400"> {log.sessionId}</span>}
                      <span> {log.message}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </main>
      <footer className="bg-white shadow-inner p-4 text-center text-gray-600">
        <div className="flex items-center justify-center">
          <div className={`h-2 w-2 rounded-full mr-2 ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <span className="text-sm text-gray-600">
            {isConnected ? 'Connected to server' : 'Disconnected from server'}
          </span>
        </div>
        <p>© {new Date().getFullYear()} DocMJ AI Assistant | Powered by ROICX dataOps</p>
      </footer>
    </div>
  );
}

export default App;

